import { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import { Entry, Pool, PaymentSettings, OwedPerson, OwedSummary } from '../../types/pool';

/**
 * Admin Payments (Payment Tracking v2)
 *
 * Person-centric: the Owed tab groups every unpaid entry by person, so one
 * Venmo that covers three entries is settled with one click. The Paid tab is
 * the history, with un-mark to correct mistakes. Settings holds the site's
 * payment handles.
 */

type Tab = 'owed' | 'paid' | 'settings';

const METHODS: { value: string; label: string }[] = [
  { value: 'VENMO', label: 'Venmo' },
  { value: 'PAYPAL', label: 'PayPal' },
  { value: 'CASHAPP', label: 'Cash App' },
  { value: 'CASH', label: 'Cash' },
  { value: 'COMP', label: 'Comp (free entry)' },
  { value: 'OTHER', label: 'Other' },
];

const METHOD_LABELS: Record<string, string> = {
  PAYPAL: 'PayPal',
  CASHAPP: 'Cash App',
  VENMO: 'Venmo',
  CASH: 'Cash',
  COMP: 'Comp',
  OTHER: 'Other',
};

const METHOD_BADGE_STYLES: Record<string, string> = {
  PAYPAL: 'bg-blue-100 text-blue-800',
  CASHAPP: 'bg-green-100 text-green-800',
  VENMO: 'bg-cyan-100 text-cyan-800',
  CASH: 'bg-amber-100 text-amber-800',
  COMP: 'bg-purple-100 text-purple-800',
  OTHER: 'bg-gray-100 text-gray-700',
};

const fmtMoney = (n: number) => (n % 1 === 0 ? String(n) : n.toFixed(2));

const personName = (p: OwedPerson) =>
  p.username ||
  [p.firstName, p.lastName].filter(Boolean).join(' ') ||
  p.email ||
  'Unknown';

function MethodBadge({ method }: { method?: string | null }) {
  if (!method) return <span className="text-gray-400">—</span>;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${METHOD_BADGE_STYLES[method] || 'bg-gray-100 text-gray-700'}`}>
      {METHOD_LABELS[method] || method}
    </span>
  );
}

export function AdminPayments() {
  const { getAccessTokenSilently } = useAuth0();

  const [tab, setTab] = useState<Tab>('owed');
  const [owed, setOwed] = useState<OwedSummary | null>(null);
  const [paidEntries, setPaidEntries] = useState<Entry[]>([]);
  const [pools, setPools] = useState<Pool[]>([]);
  const [settings, setSettings] = useState<PaymentSettings>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Owed-tab controls
  const [search, setSearch] = useState('');
  const [filterPoolId, setFilterPoolId] = useState('');
  const [lockedOnly, setLockedOnly] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [method, setMethod] = useState('VENMO'); // last-used mark-paid method
  const [methodMenuFor, setMethodMenuFor] = useState<string | null>(null);
  const [noteFor, setNoteFor] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  // Undo toast
  const [toast, setToast] = useState<{ message: string; entryIds: string[] } | null>(null);

  // Settings form
  const [settingsForm, setSettingsForm] = useState<PaymentSettings>({});
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const [owedRes, paidRes, poolsData, settingsRes] = await Promise.all([
        api.getOwedPayments(token),
        api.getPaymentEntries(token, undefined, 'PAID'),
        api.getPools(),
        api.getPaymentSettings(token),
      ]);
      setOwed(owedRes);
      setPaidEntries(paidRes.entries || []);
      setPools(poolsData);
      setSettings(settingsRes.settings || {});
      setSettingsForm(settingsRes.settings || {});
    } catch (err) {
      console.error('Failed to load payment data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-dismiss the undo toast
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 8000);
    return () => clearTimeout(timer);
  }, [toast]);

  async function handleMarkPaid(person: OwedPerson, chosenMethod: string, entryIds?: string[]) {
    try {
      setBusy(person.userId);
      setMethodMenuFor(null);
      const token = await getAccessTokenSilently();
      const note = (noteFor[person.userId] || '').trim() || undefined;
      const result = await api.markPaid(
        { userId: person.userId, entryIds, method: chosenMethod, note },
        token,
      );
      setMethod(chosenMethod);
      setNoteFor((prev) => ({ ...prev, [person.userId]: '' }));
      setToast({
        message: `Marked ${result.updatedCount} ${result.updatedCount === 1 ? 'entry' : 'entries'} for ${personName(person)} paid via ${METHOD_LABELS[chosenMethod] || chosenMethod}`,
        entryIds: result.entryIds,
      });
      await loadData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to mark paid');
    } finally {
      setBusy(null);
    }
  }

  async function handleUndo(entryIds: string[]) {
    try {
      const token = await getAccessTokenSilently();
      for (const entryId of entryIds) {
        await api.unmarkPaid({ entryId, note: 'Undo mark-paid' }, token);
      }
      setToast(null);
      await loadData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to undo');
    }
  }

  async function handleUnmark(entryId: string) {
    try {
      const token = await getAccessTokenSilently();
      await api.unmarkPaid({ entryId }, token);
      await loadData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to un-mark');
    }
  }

  async function handleSaveSettings() {
    try {
      setSavingSettings(true);
      const token = await getAccessTokenSilently();
      const { id, updatedAt, ...settingsToSave } = settingsForm as any;
      await api.updatePaymentSettings(settingsToSave, token);
      setSettings(settingsForm);
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 2500);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  }

  function toggleExpanded(userId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  // ---- Owed view derivation (search / pool / locked filters) ----
  const visiblePeople: OwedPerson[] = (owed?.people || [])
    .map((p) => {
      let entries = p.entries;
      if (filterPoolId) entries = entries.filter((e) => e.poolId === filterPoolId);
      if (lockedOnly) entries = entries.filter((e) => e.poolLocked);
      return {
        ...p,
        entries,
        totalOwed: entries.reduce((sum, e) => sum + e.amount, 0),
        hasLockedUnpaid: entries.some((e) => e.poolLocked),
      };
    })
    .filter((p) => p.entries.length > 0)
    .filter((p) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return [p.username, p.firstName, p.lastName, p.email, p.payCode]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });

  const feePools = pools.filter((p) => p.entryFee && Number(p.entryFee) > 0);

  if (loading && !owed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payment data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
            <p className="text-sm text-gray-500 mt-1">Who owes what — settle a whole person in one click</p>
          </div>
          <Link to="/admin" className="text-blue-600 hover:text-blue-700 text-sm font-medium">
            ← Back to Admin
          </Link>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700">
            {error}
          </div>
        )}

        {/* Summary tiles */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border-2 border-amber-200 p-4">
            <p className="text-sm text-amber-700 font-medium">Outstanding</p>
            <p className="text-2xl font-bold text-amber-600">${fmtMoney(owed?.totalOutstanding || 0)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4">
            <p className="text-sm text-gray-600 font-medium">People owing</p>
            <p className="text-2xl font-bold text-gray-800">{owed?.peopleCount || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4">
            <p className="text-sm text-gray-600 font-medium">Unpaid entries</p>
            <p className="text-2xl font-bold text-gray-800">{owed?.entryCount || 0}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-gray-200">
          {([
            ['owed', `Owed${owed && owed.peopleCount > 0 ? ` (${owed.peopleCount})` : ''}`],
            ['paid', 'Paid'],
            ['settings', 'Settings'],
          ] as [Tab, string][]).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-4 py-2 text-sm font-semibold rounded-t-lg border-b-2 -mb-px transition-colors ${
                tab === key
                  ? 'border-blue-600 text-blue-600 bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ============ OWED TAB ============ */}
        {tab === 'owed' && (
          <>
            {/* Filters */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, or pay code..."
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-64"
              />
              <select
                value={filterPoolId}
                onChange={(e) => setFilterPoolId(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
              >
                <option value="">All pools</option>
                {feePools.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <button
                onClick={() => setLockedOnly(!lockedOnly)}
                className={`text-sm px-3 py-1.5 rounded-lg font-medium border ${
                  lockedOnly
                    ? 'bg-red-50 border-red-300 text-red-700'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                }`}
              >
                🔒 Locked &amp; unpaid
              </button>
            </div>

            {/* People list */}
            {visiblePeople.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-10 text-center">
                <p className="text-4xl mb-3">🎉</p>
                <p className="text-lg font-semibold text-gray-900 mb-1">
                  {owed && owed.peopleCount > 0 ? 'No matches' : 'Nobody owes you anything'}
                </p>
                <p className="text-gray-600 mb-0">
                  {owed && owed.peopleCount > 0
                    ? 'Try clearing the search or filters.'
                    : 'Unpaid entries will show up here, grouped by person.'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {visiblePeople.map((person) => {
                  const isExpanded = expanded.has(person.userId);
                  const isBusy = busy === person.userId;
                  return (
                    <div key={person.userId} className="bg-white rounded-lg shadow-sm border border-gray-200">
                      {/* Person row */}
                      <div className="flex items-center gap-4 p-4 flex-wrap">
                        <div className="flex-1 min-w-[180px]">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-900">{personName(person)}</span>
                            {person.payCode && (
                              <span className="font-mono text-xs font-bold bg-gray-100 border border-gray-300 text-gray-700 px-2 py-0.5 rounded">
                                {person.payCode}
                              </span>
                            )}
                            {person.hasLockedUnpaid && (
                              <span className="text-xs font-semibold bg-red-100 text-red-700 border border-red-300 px-2 py-0.5 rounded-full">
                                🔒 locked pool
                              </span>
                            )}
                          </div>
                          {person.email && <p className="text-xs text-gray-500 mt-0.5">{person.email}</p>}
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-amber-600">${fmtMoney(person.totalOwed)}</p>
                          <p className="text-xs text-gray-500">
                            {person.entries.length} {person.entries.length === 1 ? 'entry' : 'entries'}
                          </p>
                        </div>

                        {/* Mark-all-paid split button */}
                        <div className="relative">
                          <div className="flex">
                            <button
                              onClick={() => handleMarkPaid(person, method)}
                              disabled={isBusy}
                              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-sm font-semibold px-4 py-2 rounded-l-lg transition-colors"
                            >
                              {isBusy ? 'Saving...' : `Mark paid · ${METHOD_LABELS[method]}`}
                            </button>
                            <button
                              onClick={() => setMethodMenuFor(methodMenuFor === person.userId ? null : person.userId)}
                              disabled={isBusy}
                              className="bg-green-700 hover:bg-green-800 disabled:bg-gray-400 text-white px-2 py-2 rounded-r-lg border-l border-green-500 text-sm"
                              aria-label="Choose payment method"
                            >
                              ▾
                            </button>
                          </div>
                          {methodMenuFor === person.userId && (
                            <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 w-44">
                              {METHODS.map((m) => (
                                <button
                                  key={m.value}
                                  onClick={() => handleMarkPaid(person, m.value)}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-green-50 first:rounded-t-lg last:rounded-b-lg"
                                >
                                  {m.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        <button
                          onClick={() => toggleExpanded(person.userId)}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          {isExpanded ? 'Hide' : 'Details'}
                        </button>
                      </div>

                      {/* Expanded: per-entry rows + note */}
                      {isExpanded && (
                        <div className="border-t border-gray-100 px-4 pb-4">
                          <table className="w-full mt-3">
                            <tbody className="divide-y divide-gray-100">
                              {person.entries.map((entry) => (
                                <tr key={entry.entryId}>
                                  <td className="py-2 text-sm text-gray-800">
                                    {entry.poolName}
                                    {entry.poolLocked && <span className="ml-2 text-xs text-red-600">🔒</span>}
                                  </td>
                                  <td className="py-2 text-sm text-gray-600">{entry.entryName || '—'}</td>
                                  <td className="py-2 text-sm text-gray-500">
                                    {new Date(entry.createdAt).toLocaleDateString()}
                                  </td>
                                  <td className="py-2 text-sm font-semibold text-gray-900 text-right">
                                    ${fmtMoney(entry.amount)}
                                  </td>
                                  <td className="py-2 text-right pl-3">
                                    <button
                                      onClick={() => handleMarkPaid(person, method, [entry.entryId])}
                                      disabled={isBusy}
                                      className="text-xs bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-3 py-1 rounded-lg font-medium"
                                      title={`Mark just this entry paid via ${METHOD_LABELS[method]}`}
                                    >
                                      Mark paid
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <input
                            type="text"
                            value={noteFor[person.userId] || ''}
                            onChange={(e) => setNoteFor((prev) => ({ ...prev, [person.userId]: e.target.value }))}
                            placeholder="Optional note saved with the next mark-paid (e.g. 'paid by his dad')"
                            maxLength={500}
                            className="mt-3 w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ============ PAID TAB ============ */}
        {tab === 'paid' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {paidEntries.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p className="text-lg font-medium">No payments recorded yet</p>
                <p className="text-sm mt-1">Entries you mark paid will appear here.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">User</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Pool</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Entry</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Method</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Paid</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Note</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paidEntries.map((entry) => (
                      <tr key={entry.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-gray-900">
                            {entry.user?.username || entry.user?.firstName || 'Unknown'}
                          </p>
                          {(entry.user as any)?.email && (
                            <p className="text-xs text-gray-500">{(entry.user as any).email}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{(entry as any).pool?.name || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{entry.entryName || '—'}</td>
                        <td className="px-4 py-3"><MethodBadge method={entry.paymentMethod} /></td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {entry.paidAt ? new Date(entry.paidAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate" title={entry.paymentNote || undefined}>
                          {entry.paymentNote || '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleUnmark(entry.id)}
                            className="text-xs border border-gray-300 text-gray-600 hover:bg-gray-100 px-3 py-1.5 rounded-lg font-medium"
                            title="Move back to Owed (mistake correction)"
                          >
                            Un-mark
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ============ SETTINGS TAB ============ */}
        {tab === 'settings' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-2xl">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Payment handles</h2>
            <p className="text-sm text-gray-500 mb-4">
              These power the pay buttons players see. Venmo links prefill the amount and the
              player's pay code; PayPal and Cash App prefill the amount.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PayPal.me link</label>
                <input
                  type="text"
                  value={settingsForm.paypalLink || ''}
                  onChange={(e) => setSettingsForm({ ...settingsForm, paypalLink: e.target.value })}
                  placeholder="https://paypal.me/yourname"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cash App handle</label>
                <input
                  type="text"
                  value={settingsForm.cashappHandle || ''}
                  onChange={(e) => setSettingsForm({ ...settingsForm, cashappHandle: e.target.value })}
                  placeholder="$YourTag"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Venmo handle</label>
                <input
                  type="text"
                  value={settingsForm.venmoHandle || ''}
                  onChange={(e) => setSettingsForm({ ...settingsForm, venmoHandle: e.target.value })}
                  placeholder="@YourName"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2 rounded-lg"
                >
                  {savingSettings ? 'Saving...' : 'Save settings'}
                </button>
                {settingsSaved && <span className="text-sm text-green-600 font-medium">✓ Saved</span>}
                <button
                  onClick={() => setSettingsForm(settings)}
                  className="text-sm text-gray-500 hover:text-gray-700 font-medium"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Undo toast */}
        {toast && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white rounded-lg shadow-xl px-5 py-3 flex items-center gap-4 z-50">
            <span className="text-sm">{toast.message}</span>
            <button
              onClick={() => handleUndo(toast.entryIds)}
              className="text-sm font-bold text-amber-300 hover:text-amber-200"
            >
              Undo
            </button>
            <button
              onClick={() => setToast(null)}
              className="text-gray-400 hover:text-white text-sm"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
